import { BaseApp } from "./index";

export interface ApiCallArgs
{
    [key: string]: any;
}

export interface ApiCallResponse
{
    [key: string]: any;
    success: boolean;
}

/**
 * Vanilla JS implementation thanks to this
 * @see https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
 * @see https://stackoverflow.com/questions/41102060/typescript-extending-error-class
 * @param message 
 */
export function OfflineError(message?: string) {
    this.name = 'OfflineError';
    this.message = message || 'Offline error';
    this.stack = (new Error()).stack;
}
OfflineError.prototype = Object.create(Error.prototype);
OfflineError.prototype.constructor = OfflineError;


export default class Api
{
    private app: BaseApp;
    private baseUrl: string;
    public static PUBLIC_API: string =  '/internal/api/public/';
    public static PRIVATE_API: string = '/internal/api/private/';

    constructor(app: BaseApp, baseUrl: string = '')
    {
        this.app = app;
        this.baseUrl = baseUrl;
    }

    protected buildApiCallUrl(usePublic: boolean = true): string
    {
        return `${this.baseUrl}${(usePublic ? Api.PUBLIC_API : Api.PRIVATE_API)}?_=${Date.now()}`;
    }

    protected call<T extends ApiCallArgs, U extends ApiCallResponse>(method: string, args:T = <T>{}, usePublic: boolean = true, token?: string): PromiseLike<U>
    {
        if (!navigator.onLine)
        {
            return Promise.reject(new OfflineError('Offline'));
        }

        return new Promise<U>((resolve, reject) => 
        {
            let data = JSON.stringify({ method: method, args: args });
            let url = this.buildApiCallUrl(usePublic);

            let req = new XMLHttpRequest();

            req.addEventListener('readystatechange', () =>
            {
                if (req.readyState === 4) 
                {
                    if (req.status === 200) 
                    {
                        resolve(JSON.parse(req.responseText));
                    } 
                    else 
                    {
                        if (req.status === 0)
                        {
                            reject(new OfflineError(req.responseText || 'Unable to reach the server'));
                            return;
                        }
                        
                        console.warn(`API Call failed:  `, {
                            url: url,
                            method: method,
                            args: args,
                            usePublic: usePublic,
                            token: token,
                            responseText: req.responseText,
                            status: req.status
                        });
                        let errorMessage = null;
                        try {
                            errorMessage = `Server error: ${JSON.parse(req.responseText).error}`;
                        } catch(e) {
                            errorMessage = req.statusText;
                        }
                        reject(new Error(errorMessage));
                    }
                }
            });

            req.addEventListener('error', reject);
            req.open('POST', url);
            req.timeout = 30000;
            req.setRequestHeader('X-Requested-With', 'XMLHttpRequest');

            if (token) 
            {
                req.setRequestHeader('X-TC-TOKEN', token);
            }

            req.send(data);
        });
    }

    public public<T extends ApiCallArgs, U extends ApiCallResponse>(method: string, args: T = <T>{}): PromiseLike<U>
    {
        return this.call<T, U>(method, args, true);
    }

    public private<T extends ApiCallArgs, U extends ApiCallResponse>(method: string, args: T = <T>{}, token?: string): PromiseLike<U>
    {
        token = token != null ? token : this.app.apiToken;
        return this.call<T, U>(method, args, false, token);
    }

    public auto<T extends ApiCallArgs, U extends ApiCallResponse>(method: string, args: T = <T>{}, token?: string): PromiseLike<U>
    {
        return this.app.isAuthenticated ? this.private(method, args, token) : this.public(method, args);
    }
}